import Swatch, { SwatchType } from '@components/ui/Swatch/Swatch'
import clsx from 'clsx'
import React, {
  ChangeEvent,
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import SwiperClass from 'swiper'
import 'swiper/css'
import 'swiper/css/free-mode'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import s from './SwatchGroup.module.scss'

export interface SwatchGroupProps {
  groupName: string
  swatches: SwatchType[]
  className?: string
}

const SwatchGroup: FC<SwatchGroupProps> = forwardRef(
  ({ swatches = [], groupName, className }, ref) => {
    const [selected, setSelected] = useState<SwatchType>(
      swatches.find((x) => x.checked) ?? null,
    )
    const [hovered, setHovered] = useState<SwatchType | null>(null)
    const [swiper, setSwiper] = useState<SwiperClass>()
    const [fade, setFade] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    const handleSwatchChange = (e: ChangeEvent<HTMLInputElement>) => {
      const selectedSwatch = swatches.find(
        (swatch) => swatch.id === e.target.id,
      )
      if (selectedSwatch) {
        setSelected(selectedSwatch)
      }
    }

    const handleSwatchMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        //find the input element
        const input = e.currentTarget.querySelector('input')
        if (!input) return
        const selectedSwatch = swatches.find((swatch) => swatch.id === input.id)
        if (selectedSwatch) {
          setHovered(selectedSwatch)
        }
      },
      [swatches],
    )

    const handleSwatchMouseLeave = useCallback(() => {
      setHovered(null)
    }, [])

    useEffect(() => {
      setSelected(swatches.find((x) => x.checked) ?? null)
    }, [swatches])

    return (
      <div className={clsx(s.root, className)} ref={rootRef}>
        <ul className={s.groupLabels}>
          <li className={s.label}>
            <span className={s.labelName}>{groupName}:</span>
            <span className={s.labelValue}>
              {hovered?.value ?? selected?.value}
            </span>
          </li>
        </ul>
        <Swiper
          onInit={(swiper) => {
            const isOverflowing =
              swiper.wrapperEl.scrollWidth > swiper.wrapperEl.clientWidth

            const intialIndex = swatches.findIndex((x) => x.checked)
            if (!isOverflowing) {
              swiper.snapGrid = swiper.snapGrid.map((x) => -0)
              swiper.disable()
              setFade(false)
            } else {
              swiper.params.initialSlide = intialIndex
              swiper.slideTo(intialIndex)
              if (intialIndex === swatches.length - 1) {
                setFade(false)
              } else if (isOverflowing) {
                setFade(true)
              }
            }
            setSwiper(swiper)
          }}
          modules={[Mousewheel, FreeMode]}
          className={clsx(s.list, {
            [s.fade]: fade,
            [s.swiperVisible]: swiper,
          })}
          cssMode={true}
          freeMode={false}
          touchEventsTarget="container"
          slidesPerView={'auto'}
          spaceBetween={8}
          // initialSlide={swatches.findIndex((x) => x.checked)}
          mousewheel={{
            forceToAxis: true,
          }}
          grabCursor
          slidesPerGroupAuto
          centeredSlides
          centeredSlidesBounds
          observer
          watchSlidesProgress
          slideToClickedSlide={true}
          onSlideChange={(swip) => {
            const isOverflowing =
              swip.wrapperEl.scrollWidth > swip.wrapperEl.clientWidth
            if (swip?.isEnd) {
              setFade(false)
            }
            if (!swip?.isEnd && isOverflowing) {
              setFade(true)
            }
          }}
          breakpoints={{
            1024: {
              freeMode: {
                enabled: true,
                sticky: false,
              },
              cssMode: false,
            },
          }}
        >
          {swatches.map((swatch, index) => (
            <SwiperSlide
              className={s.swatchSlide}
              key={swatch.id + index + groupName}
            >
              <Swatch
                name={groupName}
                value={swatch.value}
                id={swatch.id}
                checked={swatch.checked}
                src={swatch.src}
                color={swatch.color}
                disabled={swatch.disabled}
                onChange={handleSwatchChange}
                onMouseEnter={handleSwatchMouseEnter}
                onMouseLeave={handleSwatchMouseLeave}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    )
  },
)
SwatchGroup.displayName = 'SwatchGroup'
export default React.memo(SwatchGroup)
